import { ChangeDetectionStrategy, Component, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree } from "@angular/forms/signals";
import { AccountSelectionService, TacosStrategiesService } from "@front/m19-services";
import { IAlertComponent, IButtonDirective, ModalService } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import {
  AsinStrategyUsageComponent,
  AsinStrategyUsageModalData,
} from "@m19-board/shared/asin-strategy-usage/asin-strategy-usage-modal.component";

export interface TacosCreateStrategyAsinsModel {
  asins: string[];
}

@Component({
  selector: "app-tacos-create-strategy-asins",
  imports: [TranslocoDirective, AsinSelectionComponent, IAlertComponent, IButtonDirective],
  host: {
    class: "block h-full min-h-0",
  },
  template: `<div class="flex h-full min-h-0 flex-col gap-4 overflow-hidden py-8" *transloco="let t">
    <h4>
      {{ t("common.asins") }}
      <span class="type-body text-muted-foreground">
        ({{ t("common.selected", { count: asins().asins().value().length }) }})
      </span>
    </h4>
    @if (unavailableAsins() && unavailableAsins()!.size) {
      <IAlert class="shrink-0" type="info" [title]="t('common.asin_used_in_other_strategies')">
        <button iButton variant="secondary" size="xs" type="button" (click)="openAsinUsageModal()">
          {{ t("common.display_unavailable_asin") }}
        </button>
      </IAlert>
    }
    <div class="min-h-0 flex-1">
      <app-asin-selection
        ineligibilityTag="SP"
        [disabledAsins]="unavailableAsins()!"
        [csvExport]="false"
        [selectedAsins]="asins().asins().value()"
        (selectedAsinsChange)="asins().asins().value.set($event)"
      />
    </div>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TacosCreateStrategyAsinsComponent {
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);

  readonly asins = input.required<FieldTree<TacosCreateStrategyAsinsModel>>();

  private readonly selectedAccount = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  unavailableAsins = toSignal(this.tacosStrategiesService.unavailableAsinsForTacos$);

  openAsinUsageModal() {
    this.modalService.openModal<AsinStrategyUsageModalData, void>(AsinStrategyUsageComponent, {
      data: {
        accountId: this.selectedAccount().accountId,
        marketplace: this.selectedAccount().marketplace,
        isTacosStrategy: true,
      },
    });
  }
}
